import {
  ESIGN_ACTIVITY_COPY,
  ESIGN_CANDIDATE_SIGNATURE_TOKEN,
  ESIGN_DOCUMENT_LIMITS,
  ESIGN_DOCUMENT_TOKENS,
} from '@/constants/esignature';
import type {
  EsignActivityDocument,
  EsignDocumentValidation,
  EsignWizardDocument,
} from '@/types/esignature';

/**
 * Strips the `{{ }}` wrapper and surrounding whitespace from a token.
 *
 * Whether the validate endpoint returns tokens bare (`docusign.candidate_signature`)
 * or braced is an open question with the backend, and the two forms must not
 * compare unequal — a mismatch would silently break the candidate-anchor check,
 * which is the one rule that decides whether a candidate can sign at all. So
 * every token is normalised on the way in and re-braced only for display.
 */
export const normaliseToken = (raw: string): string =>
  String(raw ?? '')
    .trim()
    .replace(/^\{\{\s*/, '')
    .replace(/\s*\}\}$/, '')
    .trim();

export const formatToken = (raw: string): string =>
  `{{${normaliseToken(raw)}}}`;

// Explicitly Map<string, string>: ESIGN_DOCUMENT_TOKENS is `as const`, so an
// inferred map would only accept the six literal keys and reject a lookup of an
// arbitrary token coming back from validation.
const TOKEN_LABELS = new Map<string, string>(
  ESIGN_DOCUMENT_TOKENS.map(({ token, label }) => [token, label])
);

/**
 * Human label for a token. Known DocuSign anchors get their designed name;
 * anything else (an offer token that shouldn't be there, a typo) is title-cased
 * from its trailing segment so an invalid token still reads as something.
 */
export const tokenDisplayName = (raw: string): string => {
  const token = normaliseToken(raw);
  const known = TOKEN_LABELS.get(token);
  if (known) return known;
  const field = token.includes('.')
    ? token.split('.').slice(1).join('.')
    : token;
  return field
    .replace(/[_.]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

let documentKeySeed = 0;
/** Stable list key — new rows have no server id, and filenames can collide. */
export const nextDocumentKey = (): string => {
  documentKeySeed += 1;
  return `esign-doc-${documentKeySeed}`;
};

/**
 * A just-dropped file has no stored URL, so it gets an object URL here — at the
 * one point the File enters the wizard, rather than in the viewer, which would
 * have to mint one during render and revoke it on every re-key. The wizard
 * revokes it when the row is removed or the wizard unmounts.
 */
export const toWizardDocumentFromFile = (file: File): EsignWizardDocument => ({
  key: nextDocumentKey(),
  filename: file.name,
  file,
  url: URL.createObjectURL(file),
});

/** Frees the object URL a locally-added document is holding. */
export const releaseDocumentUrl = (document?: EsignWizardDocument) => {
  if (document?.file && document.url) URL.revokeObjectURL(document.url);
};

export const toWizardDocumentFromSaved = (
  document: EsignActivityDocument
): EsignWizardDocument => ({
  key: nextDocumentKey(),
  filename: document.filename,
  id: document.id,
  url: document.url,
  // A document already persisted passed validation when it was saved, so it
  // renders verified rather than unknown while the wizard has no fresh result.
  // The anchors come back on the document itself, so the token list is real
  // rather than empty until someone re-uploads the file.
  validation: {
    filename: document.filename,
    status: 'verified',
    valid_tokens: document.tokens,
  },
});

export const isDocumentInvalid = (document: EsignWizardDocument): boolean =>
  document.validation?.status === 'error';

export const validTokensOf = (validation?: EsignDocumentValidation): string[] =>
  (validation?.valid_tokens ?? []).map(normaliseToken);

export const invalidTokensOf = (
  validation?: EsignDocumentValidation
): string[] => (validation?.invalid_tokens ?? []).map(normaliseToken);

/**
 * Does the document set carry a candidate signature anchor anywhere?
 *
 * The rule is set-level, not per file: one signature page plus three
 * informational attachments is valid. DocuSign places a signing box only where
 * an anchor matches, so a set without this token produces an envelope the
 * candidate cannot sign, and the server rejects the save with
 * err_esign_candidate_signature_missing.
 *
 * A restored document normally carries its anchors, so the real check runs. The
 * fallback below is for a payload that omits them: those were validated when
 * they were saved, so the set is trusted rather than blocked on evidence the
 * wizard does not have.
 */
export const hasCandidateSignatureAnchor = (
  documents: EsignWizardDocument[]
): boolean => {
  if (documents.length === 0) return false;
  const anyRestoredWithoutTokens = documents.some(
    (document) => !document.file && !document.validation?.valid_tokens
  );
  if (anyRestoredWithoutTokens) return true;
  return documents.some((document) =>
    validTokensOf(document.validation).includes(ESIGN_CANDIDATE_SIGNATURE_TOKEN)
  );
};

/**
 * Why the recruiter cannot leave step 1 yet, or null when they can.
 *
 * Zero documents blocks here even though the design leaves Next enabled: an
 * empty `esign_documents[]` is dropped by multipart, so a zero-document save is
 * a silent no-op rather than a visible error. Catching it in the wizard is the
 * only place it reads as a real answer.
 */
export const getDocumentsBlocker = (
  documents: EsignWizardDocument[]
): string | null => {
  if (documents.length === 0) return ESIGN_ACTIVITY_COPY.DOCUMENTS_REQUIRED;
  if (documents.length > ESIGN_DOCUMENT_LIMITS.MAX_FILES) {
    return ESIGN_ACTIVITY_COPY.COUNT_LIMIT;
  }
  if (documents.some(isDocumentInvalid)) {
    return ESIGN_ACTIVITY_COPY.INVALID_DOCUMENTS_PRESENT;
  }
  if (!hasCandidateSignatureAnchor(documents)) {
    return ESIGN_ACTIVITY_COPY.CANDIDATE_SIGNATURE_MISSING;
  }
  return null;
};

/** Matches a validation result to its row. Filenames are the only shared key. */
export const mergeValidation = (
  documents: EsignWizardDocument[],
  results: EsignDocumentValidation[]
): EsignWizardDocument[] => {
  const byFilename = new Map<string, EsignDocumentValidation[]>();
  results.forEach((result) => {
    const bucket = byFilename.get(result.filename) ?? [];
    bucket.push(result);
    byFilename.set(result.filename, bucket);
  });

  return documents.map((document) => {
    if (!document.file) return document;
    // Duplicated filenames consume results in order, so two copies of the same
    // name don't both take the first verdict.
    const bucket = byFilename.get(document.filename);
    const result = bucket?.shift();
    return result ? { ...document, validation: result } : document;
  });
};
